import { useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import type { AnimationItem } from 'lottie-web';
import type { CommunityReactionType } from '../../interfaces/ICommunityHub';
import { REACTION_ASSETS } from '../../utils/reactionAssets';
import { reactionTokens } from '../../theme/tokens/reactions';

interface AnimatedReactionIconProps {
    reaction: CommunityReactionType;
    size?: number;
    animate?: boolean;
    animateOnHover?: boolean;
    loop?: boolean;
    onAnimationComplete?: () => void;
}

const animationData = new Map<CommunityReactionType, Promise<unknown>>();

export function AnimatedReactionIcon({ 
    reaction, 
    size = reactionTokens.summary,
    animate = false,
    animateOnHover = false,
    loop = false,
    onAnimationComplete,
}: AnimatedReactionIconProps) {
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [isHovering, setIsHovering] = useState(false);
    const [ready, setReady] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const completeRef = useRef(onAnimationComplete);
    useEffect(() => { completeRef.current = onAnimationComplete; }, [onAnimationComplete]);
    const shouldAnimate = (animate || (animateOnHover && isHovering)) && !prefersReducedMotion;

    useEffect(() => {
        if (!shouldAnimate || !containerRef.current) return;
        const container = containerRef.current;
        let cancelled = false;
        let player: AnimationItem | undefined;
        if (!animationData.has(reaction)) {
            animationData.set(reaction, fetch(`${import.meta.env.BASE_URL}assets/reactions/attendin/${reaction.toLowerCase()}.json`)
                .then(response => {
                    if (!response.ok) throw new Error('Reaction animation unavailable');
                    return response.json();
                }).catch(error => { animationData.delete(reaction); throw error; }));
        }
        void Promise.all([import('lottie-web'), animationData.get(reaction)]).then(([module, data]) => {
            if (cancelled) return;
            player = module.default.loadAnimation({ container, renderer: 'svg', loop, autoplay: false, animationData: structuredClone(data) });
            player.addEventListener('DOMLoaded', () => { if (!cancelled) { setReady(true); player?.play(); } });
            player.addEventListener('complete', () => completeRef.current?.());
        }).catch(() => { if (!cancelled) setReady(false); });
        return () => { cancelled = true; player?.destroy(); setReady(false); };
    }, [reaction, shouldAnimate, loop]);

    return (
        <Box
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            aria-hidden
            sx={{
                width: size,
                height: size,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flexShrink: 0,
            }}
        >
            <Box component="img" src={REACTION_ASSETS[reaction]} alt="" sx={{ width: '100%', height: '100%', visibility: ready && shouldAnimate ? 'hidden' : 'visible' }} />
            <Box ref={containerRef} sx={{ position: 'absolute', inset: 0, visibility: ready && shouldAnimate ? 'visible' : 'hidden' }} />
        </Box>
    );
}
